import { useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraGrid,
  ChakraGridItem,
} from '@equip.health/ui';
import { Form, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { useLatestNoteTemplateRevisionsWithLegacyHandling } from '~/components/maud/patients/note/useLatestNoteTemplateRevisionsWithLegacyHandling';
import { AttachmentType } from '~/lib/constants/AddPatientNoteModal.constants';
import { AppointmentStatus } from '~/lib/constants/calendar.constants';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { useGetAdminNoteActivityWithNamesEnumQuery } from '~/store/services/enumerations';
import { useGetAppointmentDetailsQuery } from '~/store/services/appointment';
import { isBaseQueryError } from '~/lib/types/api/isQueryError';
import { PatientEditNoteFormAttributesRow } from '~/components/maud/patients/note/PatientEditNoteFormAttributesRow';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { ErrorBoundary } from 'react-error-boundary';
import { TreatmentOverviewCardErrorFallback } from '~/components/common/TreatmentOverviewCard/TreatmentOverviewCardErrorFallback';
import { TreatmentOverviewCard } from '~/components/common/TreatmentOverviewCard';
import { EditPatientNoteFormAvatarCard } from './EditPatientNoteFormAvatarCard';
import { EditPatientNoteFormTitleRow } from '~/components/maud/patients/note/EditPatientNoteFormTitleRow';
import { EditPatientNoteFormContentSection } from '~/components/maud/patients/note/EditPatientNoteFormContentSection';
import OverdueAssessmentsSection from '~/components/common/OverdueAssessmentsSection/OverdueAssessmentsSection';
import { OverdueAssessmentsSectionErrorFallback } from '~/components/common/OverdueAssessmentsSection/OverdueAssessmentsSectionErrorFallback';
import { Path } from '~/routes/constants';
import { AssessmentEvents } from '~/analytics';
import {
  AssessmentEventAttribute,
  AssessmentEventSource,
} from '~/analytics/assessmentEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import NoteAttachmentCard from './NoteAttachmentCard';
export interface EditPatientNoteFormProps {
  onFormChange: () => void;
}

const TOP_LEVEL_ID = 'add-notes-form';

export const EditPatientNoteForm = ({
  onFormChange,
}: EditPatientNoteFormProps) => {
  const {
    currentUser: { userId: currentUserId },
  } = useGlobalContext();
  const { trackEvent } = useAnalytics();
  const { patientId } = useEditNoteDrawerContext();
  const { values: formValues } = useFormikContext<EditNoteFormData>();
  const { note, uploadedFiles, setUploadedFiles } =
    useEditPatientNoteFormContext();

  const isNoteAuthor = note.authoredByProviderExternalId === currentUserId;
  const [isTemplateSwitching, setIsTemplateSwitching] =
    useState<boolean>(false);

  const [clickedTemplateId, setClickedTemplateId] = useState<string>(null);

  const patientApptsCurrentDate = useMemo(
    () =>
      formValues.activityDate
        ? DateTime.fromJSDate(formValues.activityDate)
        : DateTime.now(),
    [formValues.activityDate],
  );

  const {
    data: patientAppointmentsData,
    error: patientAppointmentsError,
    isLoading: isPatientAppointmentsLoading,
  } = useGetAppointmentDetailsQuery(
    {
      patientId,
      startDateISO: patientApptsCurrentDate.minus({ days: 7 }).toISO(),
      endDateISO: patientApptsCurrentDate.plus({ days: 7 }).toISO(),
      statuses: [AppointmentStatus.COMPLETED, AppointmentStatus.SCHEDULED],
      pageSize: 100,
    },
    { skip: !patientId || !formValues },
  );

  const { data: adminNoteActivityWithNames } =
    useGetAdminNoteActivityWithNamesEnumQuery();

  const { noteTemplateRevisions, isRevisionDataLoading } =
    useLatestNoteTemplateRevisionsWithLegacyHandling(
      formValues.noteTemplateRevision?.node,
    );

  const selectedNoteTemplate = noteTemplateRevisions?.find(
    (rev) => rev.templateRevisionId === formValues.noteTemplateRevision?.node,
  )?.template;

  const activityWithReasons = useMemo<NoteMultiSelectOption[]>(
    () =>
      (adminNoteActivityWithNames ?? []).map((item) => ({
        code: item?.code,
        label: item.activityWithName,
        value: item.activityWithName,
      })),
    [adminNoteActivityWithNames],
  );

  return (
    <Form
      onChange={onFormChange}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <ChakraBox paddingX="30px">
        <EditPatientNoteFormTitleRow
          activityWithOptions={activityWithReasons}
          clickedTemplateId={clickedTemplateId}
          id={TOP_LEVEL_ID}
          isPatientAppointmentsLoading={isPatientAppointmentsLoading}
          isRevisionDataLoading={isRevisionDataLoading}
          noteTemplateRevisions={noteTemplateRevisions}
          onFormChange={onFormChange}
          patientAppointmentsData={patientAppointmentsData}
          patientAppointmentsError={
            isBaseQueryError(patientAppointmentsError)
              ? patientAppointmentsError.data?.toString()
              : undefined
          }
          setClickedTemplateId={setClickedTemplateId}
          setIsTemplateSwitching={setIsTemplateSwitching}
        />
        <PatientEditNoteFormAttributesRow
          activityWithOptions={activityWithReasons}
          attachedDocuments={uploadedFiles}
          id={TOP_LEVEL_ID}
          isNoteAuthor={isNoteAuthor}
          onFormChange={onFormChange}
          onUploadSuccess={(docs: AttachmentType[]) => {
            setUploadedFiles((prev) => [...prev, ...docs]);
          }}
          patientId={patientId}
        />
      </ChakraBox>
      <ChakraGrid
        autoRows="1fr auto"
        height="100%"
        overflowY="hidden"
        templateAreas={`"content sidebar" "files files"`}
        templateColumns="1fr auto"
      >
        <ChakraGridItem
          gridArea="content"
          paddingX="30px"
          overflowY="auto"
          overflowX="hidden"
        >
          <EditPatientNoteFormContentSection
            clickedTemplateId={clickedTemplateId}
            isTemplateSwitching={isTemplateSwitching}
            onFormChange={onFormChange}
            setClickedTemplateId={setClickedTemplateId}
            setIsTemplateSwitching={setIsTemplateSwitching}
          />
        </ChakraGridItem>

        <ChakraGridItem gridArea="sidebar" paddingRight="30px" overflowY="auto">
          <ChakraFlex flexDirection="column">
            <EditPatientNoteFormAvatarCard patientId={patientId} />
            {selectedNoteTemplate?.showOverdueAssessments && (
                <ChakraBox marginTop="16px">
                  <ErrorBoundary
                    fallback={<OverdueAssessmentsSectionErrorFallback />}
                  >
                    <OverdueAssessmentsSection
                      headerVariant="bodyMedium"
                      onClickCompleteTogether={(
                        surveyResponse: SurveyResponse,
                      ) => {
                        trackEvent(
                          AssessmentEvents.completeTogetherAssessmentCardOverdueButtonClicked,
                          {
                            [AssessmentEventAttribute.SurveyId]:
                              surveyResponse.surveyExternalId,
                            [AssessmentEventAttribute.SurveyName]:
                              surveyResponse.surveyName,
                            [AssessmentEventAttribute.Source]:
                              AssessmentEventSource.EditPatientNoteForm,
                            [AssessmentEventAttribute.ResponseExternalId]:
                              surveyResponse.responseExternalId,
                            [AssessmentEventAttribute.UserId]:
                              surveyResponse.userId,
                          },
                        );
                        window.open(
                          `${window.location.origin}${Path.MAUD_SURVEYS}/${surveyResponse.responseExternalId}`,
                          '_blank',
                        );
                      }}
                      patientId={patientId}
                    />
                  </ErrorBoundary>
                </ChakraBox>
              )}
            {selectedNoteTemplate?.showTreatmentOverview && (
              <ErrorBoundary fallback={<TreatmentOverviewCardErrorFallback />}>
                <TreatmentOverviewCard
                  noteId={note.noteExternalId}
                  patientId={patientId}
                  style={{ titleVariant: 'bodyMedium' }}
                />
              </ErrorBoundary>
            )}
          </ChakraFlex>
        </ChakraGridItem>

        {uploadedFiles.length > 0 && (
          <ChakraGridItem
            gridArea="files"
            marginTop="0.5em"
            maxHeight="144px"
            overflowY="auto"
            backgroundColor="neutral.background.primary"
            borderTop="1px solid"
            borderColor="neutral.line"
            paddingTop="12px"
            paddingX="18px"
          >
            <ChakraGrid
              gridAutoRows="1fr"
              gridTemplateColumns="repeat(3, 320px)"
              gap="12px"
            >
              {uploadedFiles.map((doc) => (
                <NoteAttachmentCard
                  document={doc}
                  isNoteAuthor={isNoteAuthor}
                  key={`attachment-${doc.attachmentExternalId}`}
                  patientId={patientId}
                  setUploadedFiles={setUploadedFiles}
                />
              ))}
            </ChakraGrid>
          </ChakraGridItem>
        )}
      </ChakraGrid>
    </Form>
  );
};
